import { useCallback, useState } from 'react';

import { useAtom, useAtomValue } from 'jotai';
import { useNavigate } from 'react-router';

import { ComparisonSwingModal, PlaybackScrubberThin, VisualizationsModal } from '@blocks';
import { VisualizerMeasurements } from '@elva-visualizer';
import { useToggle } from '@hooks';
import { useTranslate } from '@i18n';
import { useUpdateUserSettings } from '@queries';
import { activeUserIdAtom, activeUserSettingsAtom, hasFullAnalysisAtom, showVisualizationsAtom } from '@store';
import { Box, Text, ToggleButtonV2, u } from '@ui';

import { MediaVariant, MediaVariantControl, mediaVariants } from './MediaVariant';
import { PlaybackHeader } from './PlaybackHeader';
import { PlaybackLayout, PlaybackLayoutBody, PlaybackLayoutFooter, PlaybackLayoutHeader } from './PlaybackLayout';
import * as css from './PlaybackScreen.css';
import { PlaybackVideo } from './PlaybackVideo';

export function Playback() {
    const navigate = useNavigate();

    const onBackButtonClick = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    const [visualizations, toggleVisualizations] = useToggle();
    const [comparison, toggleComparison] = useToggle();

    const userSettings = useAtomValue(activeUserSettingsAtom);

    const displayMode = userSettings.showComparison ? 'comparison' : 'playback';

    const t = useTranslate();

    const activeUserId = useAtomValue(activeUserIdAtom);
    const { mutateAsync: upsertUserSettings } = useUpdateUserSettings(activeUserId);
    const handleToggleComparison = useCallback(
        (isActive: boolean) => {
            upsertUserSettings({ showComparison: isActive });
        },
        [upsertUserSettings],
    );

    const [showVisualizations, setShowVisualizations] = useAtom(showVisualizationsAtom);
    const [variant, setVariant] = useState<MediaVariant>(mediaVariants.FULL);
    const hasFullAnalysis = useAtomValue(hasFullAnalysisAtom);

    return (
        <PlaybackLayout>
            <PlaybackLayoutBody>
                <div className={css.replay}>
                    <PlaybackVideo displayMode={displayMode} variant={variant} />
                </div>
            </PlaybackLayoutBody>
            <PlaybackLayoutHeader>
                <PlaybackHeader onBack={onBackButtonClick} />
                <Box display="flex" gap={u(1)}>
                    {hasFullAnalysis && (
                        <ToggleButtonV2
                            isActive={showVisualizations}
                            onChange={setShowVisualizations}
                            onClick={() => toggleVisualizations(true)}
                        >
                            <Text variant="h3">{t('common.visualizations')}</Text>
                        </ToggleButtonV2>
                    )}
                    <ToggleButtonV2
                        isActive={userSettings.showComparison}
                        onChange={handleToggleComparison}
                        onClick={() => toggleComparison(true)}
                    >
                        <Text variant="h3">{t('common.comparison')}</Text>
                    </ToggleButtonV2>
                    <MediaVariantControl variant={variant} setVariant={setVariant} />
                </Box>
            </PlaybackLayoutHeader>
            {hasFullAnalysis && showVisualizations && (
                <Box position="absolute" top={64} right={0}>
                    <VisualizerMeasurements />
                </Box>
            )}
            <PlaybackLayoutFooter>
                <div className={css.replayScrubber}>
                    <PlaybackScrubberThin syncOnly={displayMode === 'playback'} />
                </div>
            </PlaybackLayoutFooter>
            {visualizations && <VisualizationsModal onClose={() => toggleVisualizations(false)} />}
            {comparison && <ComparisonSwingModal open onDismiss={() => toggleComparison(false)} />}
        </PlaybackLayout>
    );
}
